export interface RuntimeConfig {
  apiBaseUrl: string;
  awsRegion: string;
  cognitoUserPoolId: string;
  cognitoUserPoolClientId: string;
  cognitoDomain?: string;
  adminGroupName: string;
  clientAdminGroupName: string;
  clientReadonlyGroupName: string;
  aiAssistantAdminPortalEnabled?: boolean;
  outreachTrackingEnabled?: boolean;
  aiAssistantClientPortalEnabled?: boolean;
  aiAssistantClientMode?: string;
}

declare global {
  interface Window {
    __SURELINC_CMMC_CONFIG__?: Partial<RuntimeConfig>;
  }
}

function readEnv(name: string): string | undefined {
  const value = import.meta.env[name];
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
}

function boolConfig(value: unknown, defaultValue = false): boolean {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (["true", "1", "yes", "enabled"].includes(normalized)) return true;
    if (["false", "0", "no", "disabled"].includes(normalized)) return false;
  }
  return defaultValue;
}

function normalizeApiBaseUrl(value: string): string {
  return value.replace(/\/+$/, "");
}

export function getRuntimeConfig(): RuntimeConfig {
  const windowConfig = window.__SURELINC_CMMC_CONFIG__ ?? {};

  const apiBaseUrl =
    windowConfig.apiBaseUrl ??
    readEnv("VITE_API_BASE_URL") ??
    "";

  const awsRegion =
    windowConfig.awsRegion ??
    readEnv("VITE_AWS_REGION") ??
    "us-east-1";

  const cognitoUserPoolId =
    windowConfig.cognitoUserPoolId ??
    readEnv("VITE_COGNITO_USER_POOL_ID") ??
    "";

  const cognitoUserPoolClientId =
    windowConfig.cognitoUserPoolClientId ??
    readEnv("VITE_COGNITO_USER_POOL_CLIENT_ID") ??
    "";

  return {
    apiBaseUrl: normalizeApiBaseUrl(apiBaseUrl),
    awsRegion,
    cognitoUserPoolId,
    cognitoUserPoolClientId,
    cognitoDomain: windowConfig.cognitoDomain ?? readEnv("VITE_COGNITO_DOMAIN"),
    adminGroupName: windowConfig.adminGroupName ?? readEnv("VITE_ADMIN_GROUP_NAME") ?? "SURELINC_ADMIN",
    clientAdminGroupName: windowConfig.clientAdminGroupName ?? readEnv("VITE_CLIENT_ADMIN_GROUP_NAME") ?? "CLIENT_ADMIN",
    clientReadonlyGroupName: windowConfig.clientReadonlyGroupName ?? readEnv("VITE_CLIENT_READONLY_GROUP_NAME") ?? "CLIENT_READONLY",
    aiAssistantAdminPortalEnabled: boolConfig(windowConfig.aiAssistantAdminPortalEnabled ?? readEnv("VITE_ENABLE_AI_ASSISTANT"), true),
    outreachTrackingEnabled: boolConfig(windowConfig.outreachTrackingEnabled ?? readEnv("VITE_ENABLE_OUTREACH_TRACKING"), false),
    aiAssistantClientPortalEnabled: boolConfig(windowConfig.aiAssistantClientPortalEnabled ?? readEnv("VITE_ENABLE_CLIENT_AI_ASSISTANT"), false),
    aiAssistantClientMode: windowConfig.aiAssistantClientMode ?? readEnv("VITE_CLIENT_AI_ASSISTANT_MODE") ?? "disabled",
  };
}

export function assertRuntimeConfig(config: RuntimeConfig = getRuntimeConfig()): void {
  const missing: string[] = [];

  if (!config.apiBaseUrl) missing.push("apiBaseUrl or VITE_API_BASE_URL");
  if (!config.cognitoUserPoolId) missing.push("cognitoUserPoolId or VITE_COGNITO_USER_POOL_ID");
  if (!config.cognitoUserPoolClientId) missing.push("cognitoUserPoolClientId or VITE_COGNITO_USER_POOL_CLIENT_ID");

  if (missing.length > 0) {
    throw new Error(`Missing frontend runtime configuration: ${missing.join(", ")}`);
  }
}
